import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS as dndCSS } from '@dnd-kit/utilities';
import { DollarSign, Calendar, Link as LinkIcon } from 'lucide-react';

const TYPE_LABELS = {
  takeout: 'Takeout',
  expansion: 'Expansion',
  defense: 'Defense',
  custom: 'Custom',
};

function formatCurrency(num) {
  if (num == null) return '';
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function InitiativeCard({ initiative, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: initiative.id });

  const style = {
    transform: dndCSS.Transform.toString(transform),
    transition,
  };

  // Calculate milestone progress
  const milestones = initiative.milestones || [];
  const completedMilestones = milestones.filter(m => m.completed_at).length;
  const progressPct = milestones.length > 0
    ? Math.round((completedMilestones / milestones.length) * 100)
    : 0;

  // Calculate linked renewals total
  const linkedRenewals = initiative.linked_renewals || [];
  const renewalsTotal = linkedRenewals.reduce((sum, r) => sum + (Number(r.value) || 0), 0);

  const handleClick = (e) => {
    // Don't open modal if dragging
    if (isDragging) return;
    onClick?.(initiative);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`si-card ${isDragging ? 'dragging' : ''}`}
      onClick={handleClick}
      {...attributes}
      {...listeners}
    >
      <div className="si-card__header">
        <div className="si-card__title">{initiative.title}</div>
        <span className={`si-card__type si-card__type--${initiative.initiative_type}`}>
          {TYPE_LABELS[initiative.initiative_type]}
        </span>
      </div>

      <div className="si-card__meta">
        {initiative.target_value && (
          <span>
            <DollarSign size={12} />
            {formatCurrency(initiative.target_value)}
          </span>
        )}
        {initiative.target_end_date && (
          <span>
            <Calendar size={12} />
            {formatDate(initiative.target_end_date)}
          </span>
        )}
      </div>

      {milestones.length > 0 && (
        <div className="si-card__progress">
          <div className="si-card__progress-bar">
            <div
              className="si-card__progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span>{completedMilestones}/{milestones.length}</span>
        </div>
      )}

      {linkedRenewals.length > 0 && (
        <div className="si-card__renewals">
          <LinkIcon size={12} />
          {linkedRenewals.length} renewal{linkedRenewals.length !== 1 ? 's' : ''} linked
          {renewalsTotal > 0 && ` · ${formatCurrency(renewalsTotal)}`}
        </div>
      )}
    </div>
  );
}

export default InitiativeCard;
